import { useEffect, useMemo } from "react";

const GLASS_SURFACE_FILTER_ID = "denker-glass-surface-filter";
const SVG_GLASS_ATTRIBUTE = "data-denker-svg-glass";

function supportsSVGBackdropFilter(filterId: string) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent;
  const isChromium =
    /(Chrome|Chromium|Edg|OPR|SamsungBrowser)\//.test(userAgent) &&
    !/(CriOS|EdgiOS|OPiOS)/.test(userAgent);
  const isWebkit = /AppleWebKit/.test(userAgent) && !isChromium;
  const isFirefox = /(Firefox|FxiOS)/.test(userAgent);
  if (isWebkit || isFirefox) return false;

  const probe = document.createElement("div");
  probe.style.backdropFilter = `url(#${filterId})`;
  return probe.style.backdropFilter !== "";
}

function buildDisplacementMap() {
  const width = 300;
  const height = 300;
  const radius = 28;
  const edgeSize = 10;
  const map = `
    <svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="denker-glass-map-red" x1="100%" y1="0%" x2="0%" y2="0%">
          <stop offset="0%" stop-color="#0000"/>
          <stop offset="100%" stop-color="red"/>
        </linearGradient>
        <linearGradient id="denker-glass-map-blue" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="#0000"/>
          <stop offset="100%" stop-color="blue"/>
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="${width}" height="${height}" fill="black"/>
      <rect x="0" y="0" width="${width}" height="${height}" rx="${radius}" fill="url(#denker-glass-map-red)"/>
      <rect x="0" y="0" width="${width}" height="${height}" rx="${radius}" fill="url(#denker-glass-map-blue)" style="mix-blend-mode:screen"/>
      <rect x="${edgeSize}" y="${edgeSize}" width="${width - edgeSize * 2}" height="${height - edgeSize * 2}" rx="${radius}" fill="hsl(0 0% 56% / 0.92)" style="filter:blur(11px)"/>
    </svg>
  `;
  return `data:image/svg+xml,${encodeURIComponent(map)}`;
}

function GlassSurfaceFilter() {
  const mapHref = useMemo(buildDisplacementMap, []);

  useEffect(() => {
    if (supportsSVGBackdropFilter(GLASS_SURFACE_FILTER_ID)) {
      document.documentElement.setAttribute(SVG_GLASS_ATTRIBUTE, "true");
    } else {
      document.documentElement.removeAttribute(SVG_GLASS_ATTRIBUTE);
    }
  }, []);

  return (
    <svg
      className="denker-glass-filter-defs"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter
          id={GLASS_SURFACE_FILTER_ID}
          colorInterpolationFilters="sRGB"
          x="0%"
          y="0%"
          width="100%"
          height="100%"
        >
          <feImage
            href={mapHref}
            x="0"
            y="0"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            result="map"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="map"
            scale="-170"
            xChannelSelector="R"
            yChannelSelector="G"
            result="dispRed"
          />
          <feColorMatrix
            in="dispRed"
            type="matrix"
            values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
            result="red"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="map"
            scale="-160"
            xChannelSelector="R"
            yChannelSelector="G"
            result="dispGreen"
          />
          <feColorMatrix
            in="dispGreen"
            type="matrix"
            values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
            result="green"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="map"
            scale="-150"
            xChannelSelector="R"
            yChannelSelector="G"
            result="dispBlue"
          />
          <feColorMatrix
            in="dispBlue"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
            result="blue"
          />
          <feBlend in="red" in2="green" mode="screen" result="rg" />
          <feBlend in="rg" in2="blue" mode="screen" result="output" />
          <feGaussianBlur in="output" stdDeviation="0.7" />
        </filter>
      </defs>
    </svg>
  );
}

export { GlassSurfaceFilter };
